import type { EmployerTodayTaskItem } from '@fdh/shared';
import { APPROVAL_STATUS, intlLocaleForLanguage, isSupportedLanguage, LANGUAGE, type ApprovalStatus, type Language } from '@fdh/shared';

const toAppLanguage = (language: string): Language =>
  isSupportedLanguage(language) ? language : LANGUAGE.ZH_HANT;

export type EmployerDayTimeBucket = 'lateNight' | 'morning' | 'afternoon' | 'evening' | 'noTime';

export const EMPLOYER_TIME_BUCKET_I18N: Record<EmployerDayTimeBucket, string> = {
  lateNight: 'task.list.timeSectionLateNight',
  morning: 'task.list.timeSectionMorning',
  afternoon: 'task.list.timeSectionAfternoon',
  evening: 'task.list.timeSectionEvening',
  noTime: 'task.list.timeSectionNoTime',
};

type TaskWithDueTimeAndTitle = { dueTime: string; title: string };

type TaskWithDueDateAndTime = TaskWithDueTimeAndTitle & { dueDate: string };

export const ISO_YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

export const isIsoYmd = (value: string | null): value is string =>
  Boolean(value && ISO_YMD_RE.test(value));

export const parseEmployerDueTimeToMinutes = (dueTime: string): number | null => {
  const trimmed = dueTime.trim();
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) {
    return null;
  }
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    return null;
  }
  return hour * 60 + minute;
};

export const resolveEmployerDayTimeBucket = (dueTime: string): EmployerDayTimeBucket => {
  const minutes = parseEmployerDueTimeToMinutes(dueTime);
  if (minutes === null) {
    return 'noTime';
  }
  if (minutes <= 4 * 60 + 59) {
    return 'lateNight';
  }
  if (minutes <= 11 * 60 + 59) {
    return 'morning';
  }
  if (minutes <= 17 * 60 + 59) {
    return 'afternoon';
  }
  return 'evening';
};

const BUCKET_ORDER: EmployerDayTimeBucket[] = [
  'lateNight',
  'morning',
  'afternoon',
  'evening',
  'noTime',
];

export const compareTasksByDueTime = <T extends TaskWithDueTimeAndTitle>(a: T, b: T): number => {
  const ma = a.dueTime ? parseEmployerDueTimeToMinutes(a.dueTime) : null;
  const mb = b.dueTime ? parseEmployerDueTimeToMinutes(b.dueTime) : null;
  if (ma === null && mb === null) {
    return a.title.localeCompare(b.title);
  }
  if (ma === null) {
    return 1;
  }
  if (mb === null) {
    return -1;
  }
  if (ma !== mb) {
    return ma - mb;
  }
  return a.title.localeCompare(b.title);
};

export const compareEmployerDayTasksByDueTime = (
  a: EmployerTodayTaskItem,
  b: EmployerTodayTaskItem,
): number => compareTasksByDueTime(a, b);

export const groupEmployerTasksByDueDateNewestFirst = <T extends TaskWithDueDateAndTime>(
  tasks: T[],
): Array<{ date: string; tasks: T[] }> => {
  const byDate = new Map<string, T[]>();
  tasks.forEach((task) => {
    const list = byDate.get(task.dueDate) ?? [];
    list.push(task);
    byDate.set(task.dueDate, list);
  });
  const dates = Array.from(byDate.keys()).sort((a, b) => (a < b ? 1 : a > b ? -1 : 0));
  return dates.map((date) => ({
    date,
    tasks: (byDate.get(date) ?? []).slice().sort(compareTasksByDueTime),
  }));
};

export const sortEmployerTasksByDueDateNewestFirst = <T extends TaskWithDueDateAndTime>(
  tasks: T[],
): T[] => groupEmployerTasksByDueDateNewestFirst(tasks).flatMap((group) => group.tasks);

export const groupTasksByTimeBucket = <T extends TaskWithDueTimeAndTitle>(
  tasks: T[],
): Array<{ bucket: EmployerDayTimeBucket; tasks: T[] }> =>
  BUCKET_ORDER.map((bucket) => ({
    bucket,
    tasks: tasks
      .filter((task) => resolveEmployerDayTimeBucket(task.dueTime) === bucket)
      .slice()
      .sort((a, b) => compareTasksByDueTime(a, b)),
  })).filter((group) => group.tasks.length > 0);

export const groupEmployerDayTasksByTimeBucket = (
  tasks: EmployerTodayTaskItem[],
): Array<{ bucket: EmployerDayTimeBucket; tasks: EmployerTodayTaskItem[] }> =>
  groupTasksByTimeBucket(tasks);

export const addCalendarDaysToYmd = (ymd: string, deltaDays: number): string => {
  const [yRaw, mRaw, dRaw] = ymd.split('-');
  const y = Number(yRaw);
  const m = Number(mRaw);
  const d = Number(dRaw);
  const base = new Date(y, m - 1, d);
  base.setDate(base.getDate() + deltaDays);
  const yy = base.getFullYear();
  const mm = String(base.getMonth() + 1).padStart(2, '0');
  const dd = String(base.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
};

/** Inclusive calendar offsets from today: -2 (today-2) through +7 (today+7). */
export const buildEmployerDateStripDays = (todayYmd: string): string[] => {
  const days: string[] = [];
  for (let offset = -2; offset <= 7; offset += 1) {
    days.push(addCalendarDaysToYmd(todayYmd, offset));
  }
  return days;
};

export const getDateInTimezone = (timezone: string): string => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const year = parts.find((part) => part.type === 'year')?.value ?? '';
  const month = parts.find((part) => part.type === 'month')?.value ?? '';
  const day = parts.find((part) => part.type === 'day')?.value ?? '';
  return `${year}-${month}-${day}`;
};

export const formatEmployerDateStripWeekday = (
  dateYmd: string,
  language: string,
): string => {
  const [y, m, d] = dateYmd.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const locale = intlLocaleForLanguage(toAppLanguage(language));
  return new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(date);
};

export const formatEmployerDateStripDayNumber = (dateYmd: string): string => {
  const day = dateYmd.slice(8, 10);
  return String(Number(day));
};

export const formatEmployerSelectedDateHeading = (
  dateYmd: string,
  language: string,
): string => {
  const [y, m, d] = dateYmd.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const appLanguage = toAppLanguage(language);
  const locale = intlLocaleForLanguage(appLanguage);
  const weekday = new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(date);
  if (appLanguage === LANGUAGE.EN || appLanguage === LANGUAGE.ID || appLanguage === LANGUAGE.FIL) {
    return `${dateYmd} (${weekday})`;
  }
  return `${dateYmd}（${weekday}）`;
};

export const isEmployerTodayTaskNotSubmittedPastDueTime = (
  task: EmployerTodayTaskItem,
  todayYmd: string,
  currentMinutes: number,
): boolean =>
  isEmployerTaskInstanceOverdue(
    {
      dueDate: task.dueDate,
      dueTime: task.dueTime,
      status: task.status,
      submittedAt: task.submittedAt,
    },
    todayYmd,
    currentMinutes,
  ) &&
  task.status === APPROVAL_STATUS.PENDING &&
  (!task.submittedAt || String(task.submittedAt).trim() === '') &&
  task.dueDate === todayYmd;

export const isEmployerTaskInstanceOverdue = (
  task: {
    dueDate: string;
    dueTime: string;
    status: ApprovalStatus;
    submittedAt: string;
  },
  todayYmd: string,
  currentMinutes: number,
): boolean => {
  const hasSubmitted = Boolean(task.submittedAt && String(task.submittedAt).trim() !== '');

  if (task.status === APPROVAL_STATUS.NEEDS_UPDATE && task.dueDate < todayYmd) {
    return true;
  }

  if (task.status !== APPROVAL_STATUS.PENDING) {
    return false;
  }

  if (task.dueDate < todayYmd) {
    return true;
  }

  if (!hasSubmitted && task.dueDate === todayYmd) {
    const dueMinutes = task.dueTime ? parseEmployerDueTimeToMinutes(task.dueTime) : null;
    if (dueMinutes !== null && dueMinutes < currentMinutes) {
      return true;
    }
  }

  return false;
};
